import React, {useState, useEffect} from "react";
import { MenuItem, Stack, TextField, Button } from "@mui/material";
import { XYPlot, LineSeries, VerticalGridLines, HorizontalGridLines, XAxis, YAxis, VerticalBarSeries, Hint } from 'react-vis';
import getFatturatoCommerciale from "../../services/getFatturatoCommerciale";
import DashboardTitle from "../../widgets/DashboardTitle";
import Utils from "../../utils/utils";

const selectAnnoMese = ['mese', 'anno'];
const selectedTipo = ['agente', 'segnalatore', 'funzionario'];
const colors = ['#FF0000','#0000FF'];
const currentYear = new Date().getFullYear();
const lastYear = currentYear - 1;

const DashboardFatturato = ({username, token, clienti, prodotti, agenti, funzionari, segnalatori, regioni, province}) => {

    const[fatturatoTotalePerAnno, setFatturatoTotalePerAnno] = useState([]);
    const [fatturatoTotaleMeseCorrente, setFatturatoTotaleMeseCorrente] = useState(null);
    const [fatturatoTotaleMesePrecedente, setFatturatoTotaleMesePrecedente] = useState(null);
    const [fatturatoError, setFatturatoError] = useState('');
    const [annoMese, setAnnoMese] = useState(selectAnnoMese[0]);
    const [chartsWidth, setChartsWidth] = useState(0);
    const [prodotto, setProdotto] = useState(null);
    const [tipo, setTipo] = useState(null);
    const [segnalatore, setSegnalatore] = useState(null);
    const [agente, setAgente] = useState(null);
    const [funzionario, setFunzionario] = useState(null);
    const [cliente, setCliente] = useState(null);
    const [regione, setRegione] = useState(null);
    const [provincia, setProvincia] = useState(null);
    const [hintValue, setHintValue] = useState(null);
    const [provinceFiltrate, setProvinceFiltrate] = useState([]);

    const getFatturato = async (prodotto, segnalatore, agente, cliente, regione, provincia, funzionario) => {
        const response = await getFatturatoCommerciale(username, token, prodotto, segnalatore, agente, tipo, cliente, regione, provincia, funzionario);  
        if(response.status === 'ok'){

            const new_fatturato_mensile_anno_corrente = [];
            const new_fatturato_mensile_anno_precedente = [];
            const new_fatturato_totale_per_anno = [];
            if(
                response.fatturato_mensile_anno_corrente !== undefined && 
                response.fatturato_mensile_anno_precedente !== undefined &&
                response.fatturato_totale_per_anno !== undefined
            ){
                for(const [key, value] of Object.entries(response.fatturato_mensile_anno_corrente)){
                    new_fatturato_mensile_anno_corrente.push({ x: key, y: value});
                }
                for(const [key, value] of Object.entries(response.fatturato_mensile_anno_precedente)){
                    new_fatturato_mensile_anno_precedente.push({ x: key, y: value });
                }
                let counter = 0;
                for(const [key, value] of Object.entries(response.fatturato_totale_per_anno)){
                    new_fatturato_totale_per_anno.push({ id: key, x: key, y: value, color: colors[counter]});
                    counter < 2 ? counter++ : counter = 0;
                }
                setFatturatoTotaleMeseCorrente(new_fatturato_mensile_anno_corrente);
                setFatturatoTotaleMesePrecedente(new_fatturato_mensile_anno_precedente);
                setFatturatoTotalePerAnno(new_fatturato_totale_per_anno);
                setFatturatoError('');
            }
        } else {
            setFatturatoError(response.errore);
        }
    };

    const onChangeCliente = (id_cliente) => {
        const cliente = clienti.find((c) => c.id === id_cliente);
        if(cliente) {
            setCliente(cliente.id);
            setRegione(cliente.regione);
            setProvincia(cliente.provincia);
        } else {
            setCliente(null);
            setRegione(null);
            setProvincia(null);
        }
    };

    const onChangeRegione = (regione) => {
        if(regione) {
            const filteredProvince = province.filter((provincia) => {
                const matchingProvince = Utils.province.find((p) => p.nome === provincia);
                return matchingProvince && matchingProvince.regione === regione;
            });
            setRegione(regione);
            setProvinceFiltrate(filteredProvince);
        } else {
            setRegione(null);
            setProvinceFiltrate(province);
        }
        setProvincia(null);
        setCliente(null);
    };

    const onChangeProvincia = (provincia) => {
        if(provincia) {
            const matchingProvince = Utils.province.find((p) => p.nome === provincia); 
            setRegione(matchingProvince.regione);
            setProvincia(provincia);
        } else {
            setProvincia(null);
            setProvinceFiltrate(province)
        }
        setCliente(null);
    };

    const onChangeTipo = (tipo) => {
        setTipo(tipo);
        setAgente(null);
        setSegnalatore(null);
        setFunzionario(null);
    };

    const clearFilter = () => {
        setProdotto(null);
        setTipo(null);
        setAgente(null);
        setSegnalatore(null);
        setFunzionario(null);
        setCliente(null);
        setRegione(null);
        setProvincia(null);
    }

    useEffect(() => {
        getFatturato();
        setChartsWidth(window.innerWidth - 360);
    }, []);

    useEffect(() => {
        getFatturato(prodotto, segnalatore, agente, cliente, regione, provincia, funzionario);
    }, [prodotto, segnalatore, agente, cliente, regione, provincia, funzionario]);

    return (
        <div style={dashboardView}>
            <DashboardTitle icon={require('../../images/andamento.png')} title="Fatturato" />
            <Stack direction="row" spacing={2}>
                <TextField
                    select
                    label="Anno/Mese"
                    helperText="Scelta anno/mese"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={annoMese}
                    onChange={(e) => setAnnoMese(e.target.value)}
                >
                    {selectAnnoMese.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Prodotto"
                    helperText="Scelta prodotto"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={prodotto}
                    onChange={(e) => setProdotto(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessun prodotto
                    </MenuItem>
                    {prodotti.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.nome}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Tipo"
                    helperText="Scelta tipo"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={tipo}
                    onChange={(e) => onChangeTipo(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessuno
                    </MenuItem>
                    {selectedTipo.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                { tipo === 'agente' && <TextField
                    select
                    label="Agente"
                    helperText="Scelta agente"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={agente}
                    onChange={(e) => setAgente(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessuno
                    </MenuItem>
                    {agenti.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.ragione_sociale}
                        </MenuItem>
                    ))}
                </TextField> }
                { tipo === 'segnalatore' && <TextField
                    select
                    label="Segnalatore"
                    helperText="Scelta segnalatore"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={segnalatore}
                    onChange={(e) => setSegnalatore(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessuno
                    </MenuItem>
                    {segnalatori.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.ragione_sociale}
                        </MenuItem>
                    ))}
                </TextField> }
                { tipo === 'funzionario' && <TextField
                    select
                    label="Funzionario"
                    helperText="Scelta funzionario"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={funzionario}
                    onChange={(e) => setFunzionario(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessuno
                    </MenuItem>
                    {funzionari.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.nome + ' ' + option.cognome}
                        </MenuItem>
                    ))}
                </TextField> }
                <TextField
                    select
                    label="Cliente"
                    helperText="Scelta cliente"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={cliente}
                    onChange={(e) => onChangeCliente(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessuno
                    </MenuItem>
                    {clienti.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.ragione_sociale}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Regione"
                    helperText="Scelta regione"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={regione}
                    onChange={(e) => onChangeRegione(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessuna
                    </MenuItem>
                    {regioni.map((regione, i) => (
                        <MenuItem key={i} value={regione}>
                            {regione}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Provincia"
                    helperText="Scelta provincia"
                    variant="standard"
                    sx={{ width: 200 }}
                    value={provincia}
                    onChange={(e) => onChangeProvincia(e.target.value)}
                >
                    <MenuItem key={0} value={null}>
                        Nessuna
                    </MenuItem>
                    {provinceFiltrate.map((provincia, i) => (
                        <MenuItem key={i} value={provincia}>
                            {provincia}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            <Button
                    style={{ paddingVertical: 10, paddingHorizontal: 20, margin: 0, height: 40, backgroundColor: '#5CB500', marginTop: 10, marginBottom: 10}}
                    onClick={clearFilter}
                    variant="contained"
                    size="large"
                    color="secondary"
                >
                    Elimina filtri
                </Button>
            <div className="monthly-revenue-chart">
                { fatturatoError === '' && annoMese === 'mese' && fatturatoTotaleMesePrecedente && fatturatoTotaleMeseCorrente  && <>
                    <XYPlot height={300} width={chartsWidth} xType="ordinal" margin={{ left: 80 }} yRange={[230, 20]}>
                        <VerticalGridLines />
                        <HorizontalGridLines />
                        <XAxis />
                        <LineSeries data={fatturatoTotaleMesePrecedente} color={colors[0]} />
                        <LineSeries data={fatturatoTotaleMeseCorrente} color={colors[1]} />
                        <YAxis style={{ width: 100 }} />
                    </XYPlot>
                    {hintValue && <Hint value={hintValue}>
                        <div style={{ background: 'white', border: '1px solid #ccc', padding: '5px' }}>
                            <p>Mese: {hintValue.x}</p>
                            <p>Fatturato: {hintValue.y}</p>
                        </div>
                    </Hint> }
                    <Stack direction="column">
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{ backgroundColor: colors[0], width: '20px', height: '20px', display: 'inline-block', marginRight: '10px' }}></span>
                            <span>{lastYear}</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{ backgroundColor: colors[1], width: '20px', height: '20px', display: 'inline-block', marginRight: '10px' }}></span>
                            <span>{currentYear}</span>
                        </div>
                    </Stack>
                </> }
                { fatturatoError === '' && annoMese === 'anno' && fatturatoTotaleMesePrecedente && fatturatoTotaleMeseCorrente  && <>
                    <XYPlot height={300} width={chartsWidth} xType="ordinal" margin={{ left: 80 }} yRange={[230, 20]}>
                        <VerticalBarSeries data={fatturatoTotalePerAnno} colorType="literal" />
                        <XAxis />
                        <YAxis style={{ width: 100 }} />
                    </XYPlot> 
                    <Stack direction="column">
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{ backgroundColor: colors[0], width: '20px', height: '20px', display: 'inline-block', marginRight: '10px' }}></span>
                            <span>{lastYear}</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{ backgroundColor: colors[1], width: '20px', height: '20px', display: 'inline-block', marginRight: '10px' }}></span>
                            <span>{currentYear}</span>
                        </div>
                    </Stack>
                </>}
            </div>
            { fatturatoError !== '' && <div style={errorMessage}>{fatturatoError}</div> }
        </div>
    );
};

export default DashboardFatturato;

const dashboardView = {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 30,
    marginBottom: 10,
    width: '100%',
    overflow: 'hidden',
    boxShadow: '0px 2px 4px lightgrey',
    minHeight: 615,
    position: 'relative'
}

const errorMessage = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: 20,
    color: 'red',
    fontSize: 20,
}